import {
  Box,
  IconButton,
  Typography,
  Slide,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CaretLineRightIcon, CaretLineLeftIcon } from '@phosphor-icons/react';
import { ReactNode, useState, useEffect } from 'react';

export interface LowLevelDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string | number;
  anchor?: 'primary' | 'secondary';
  hideBackdrop?: boolean;
  disableBackdropClick?: boolean;
}

/**
 * A low-level drawer component that provides the core drawer functionality.
 *
 * **INTERNAL USE ONLY**: This is the low-level implementation. Use the main Drawer
 * component instead, which integrates with DrawerProvider for automatic backdrop
 * and multi-drawer management.
 *
 * Features:
 * - Responsive design (full width on mobile, configurable width on desktop)
 * - Smooth slide animations
 * - Consistent header layout with title and close button
 * - Scrollable content area
 */
export const LowLevelDrawer = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = '50%',
  anchor = 'primary',
  hideBackdrop = false,
  disableBackdropClick = false,
}: LowLevelDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [shouldRender, setShouldRender] = useState(open);

  // On mobile, always use full width; otherwise use the provided width
  const effectiveWidth = isMobile ? '100%' : width;
  // On mobile, always slide from right for consistent UX; otherwise use anchor-based direction
  const slideDirection = isMobile
    ? 'left'
    : anchor === 'primary'
      ? 'left'
      : 'right';
  // On mobile, always position from left (full width); otherwise use anchor positioning
  const positionStyle = isMobile
    ? { left: 0, right: 0 }
    : anchor === 'primary'
      ? { right: 0 }
      : { left: 0 };

  // Handle mounting/unmounting with proper animation timing
  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  const handleExited = () => {
    setShouldRender(false);
  };

  if (!shouldRender) return null;

  const isFixedPixelWidth =
    typeof width === 'number' ||
    (typeof width === 'string' && /^\d+px$/i.test(width));

  const roundedBorderStyle = isFixedPixelWidth
    ? {}
    : anchor === 'primary'
      ? { borderRadius: '16px 0 0 16px' }
      : { borderRadius: '0 16px 16px 0' };

  return (
    <>
      {/* Custom backdrop with fade animation */}
      {!hideBackdrop && (
        <Fade in={open} timeout={225}>
          <Box
            onClick={disableBackdropClick ? undefined : onClose}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: theme => theme.zIndex.drawer - 1,
              cursor: disableBackdropClick ? 'default' : 'pointer',
            }}
          />
        </Fade>
      )}

      {/* Drawer container */}
      <Slide
        direction={slideDirection}
        in={open}
        timeout={225}
        onExited={handleExited}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0, // Full height from top
            bottom: 0,
            width: effectiveWidth,
            backgroundColor: 'background.paper',
            boxShadow: theme => theme.shadows[16],
            zIndex: theme => theme.zIndex.drawer + 10,
            padding: '24px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)', // Smooth width animation
            ...positionStyle,
            ...roundedBorderStyle,
          }}
        >
          <Box
            sx={{
              height: '100%', // Account for padding (24px top + 24px bottom)
              maxHeight: '100%',
              width: '100%',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gridTemplateColumns: '1fr',
            }}
          >
            {/* Header with title and close button */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mb: 5,
              }}
            >
              <Typography variant="titleLarge">{title}</Typography>
              <IconButton onClick={onClose} sx={{ ml: 'auto' }}>
                {anchor === 'primary' ? (
                  <CaretLineRightIcon size={32} weight="bold" />
                ) : (
                  <CaretLineLeftIcon size={32} weight="bold" />
                )}
              </IconButton>
            </Box>

            {/* Content area */}
            <Box sx={{ overflow: 'auto', minHeight: 0 }}>{children}</Box>

            {/* Footer area */}
            {footer && (
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  padding: '24px 0 0 0',
                }}
              >
                {footer}
              </Box>
            )}
          </Box>
        </Box>
      </Slide>
    </>
  );
};
